import React, { useState, useEffect, useMemo } from 'react';
import { Input } from './ui/input';
import { Button } from './ui/button';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Calendar, ChevronDown, ChevronUp } from 'lucide-react';
import { cn } from '@/lib/utils';

/**
 * Genealogy-friendly date entry: typing wins; decade chips are optional shortcuts;
 * qualifiers (circa / before / …) are explicit; no aggressive reformat while typing.
 */

const QUALIFIERS = [
  { value: 'exact', label: 'Exact year' },
  { value: 'circa', label: 'Circa (about this year)' },
  { value: 'before', label: 'Before this year' },
  { value: 'after', label: 'After this year' },
  { value: 'estimated', label: 'Estimated / uncertain' },
  { value: 'unknown', label: 'Unknown exact year' },
];

const emptyState = () => ({
  year: '',
  month: '',
  day: '',
  qualifier: 'exact',
});

/** Parse stored birth_date / death_date string into editor state */
function parseStoredDate(val) {
  if (!val || !String(val).trim()) return emptyState();

  let s = String(val).trim();
  let qualifier = 'exact';

  if (/^unknown$/i.test(s)) {
    return { ...emptyState(), qualifier: 'unknown' };
  }

  if (/^BEF-/i.test(s)) {
    qualifier = 'before';
    s = s.replace(/^BEF-/i, '');
  } else if (/^AFT-/i.test(s)) {
    qualifier = 'after';
    s = s.replace(/^AFT-/i, '');
  } else if (/^EST-/i.test(s)) {
    qualifier = 'estimated';
    s = s.replace(/^EST-/i, '');
  } else if (/^ABT-/i.test(s)) {
    qualifier = 'estimated';
    s = s.replace(/^ABT-/i, '');
  }

  let circa = false;
  if (/-approx$/i.test(s)) {
    circa = true;
    s = s.replace(/-approx$/i, '');
  }
  if (s.startsWith('~')) {
    circa = true;
    s = s.slice(1);
  }
  if (circa && qualifier === 'exact') qualifier = 'circa';

  const iso = s.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (iso) {
    return {
      year: iso[1],
      month: iso[2],
      day: iso[3],
      qualifier,
    };
  }

  const ym = s.match(/^(\d{4})-(\d{2})$/);
  if (ym) {
    return { year: ym[1], month: ym[2], day: '', qualifier };
  }

  const digitsOnly = s.replace(/\D/g, '');
  if (/^\d{1,4}$/.test(digitsOnly)) {
    return {
      year: digitsOnly.slice(0, 4),
      month: '',
      day: '',
      qualifier,
    };
  }

  const y4 = s.match(/(\d{4})/);
  if (y4) {
    return { year: y4[1], month: '', day: '', qualifier };
  }

  return emptyState();
}

/** Build stored string from editor state (partial years allowed while typing) */
function buildStoredDate(state) {
  const { year, month, day, qualifier } = state;
  const raw = (year || '').replace(/\D/g, '').slice(0, 4);
  if (qualifier === 'unknown') return '';

  if (!raw) return '';

  const padM = (month || '').padStart(2, '0');
  const padD = (day || '').padStart(2, '0');
  const hasMonth = Boolean(month);
  const hasDay = Boolean(day);

  const baseExact = () => {
    if (hasMonth && hasDay) return `${raw}-${padM}-${padD}`;
    if (hasMonth) return `${raw}-${padM}-01`;
    return raw;
  };

  if (qualifier === 'before') return `BEF-${raw}`;
  if (qualifier === 'after') return `AFT-${raw}`;
  if (qualifier === 'estimated') return `EST-${raw}`;

  if (qualifier === 'circa') {
    if (hasMonth && hasDay) return `${raw}-${padM}-${padD}-approx`;
    if (hasMonth) return `${raw}-${padM}-01-approx`;
    return `${raw}-approx`;
  }

  return baseExact();
}

function decadeLabel(startYear) {
  return `${startYear}s`;
}

const SmartDateInput = ({
  value = '',
  onChange,
  label = 'Date',
  placeholder = 'Year (e.g. 1847, 1022)',
  showDeathContext = false,
  /** `simple`: year only, no certainty/decades. `full`: genealogy controls; use collapseAdvanced to tuck extras away. */
  mode = 'full',
  collapseAdvanced = false,
  'data-testid': testId = 'smart-date-input',
}) => {
  const [dateState, setDateState] = useState(() => parseStoredDate(value));
  const [showDecades, setShowDecades] = useState(false);
  const [showOlderDecades, setShowOlderDecades] = useState(false);
  const [showAncientDecades, setShowAncientDecades] = useState(false);

  const primaryDecades = useMemo(() => {
    const out = [];
    for (let y = 2020; y >= 1970; y -= 10) out.push(y);
    return out;
  }, []);

  const secondaryDecades = useMemo(() => {
    const out = [];
    for (let y = 1960; y >= 1400; y -= 10) out.push(y);
    return out;
  }, []);

  const ancientDecades = useMemo(() => {
    const out = [];
    for (let y = 1390; y >= 100; y -= 10) out.push(y);
    return out;
  }, []);

  useEffect(() => {
    const incoming = value == null ? '' : String(value).trim();
    setDateState((prev) => {
      const outgoing = buildStoredDate(prev);
      if (incoming === (outgoing ?? '')) return prev;
      return parseStoredDate(incoming);
    });
  }, [value]);

  const pushChange = (next) => {
    setDateState(next);
    onChange?.(buildStoredDate(next));
  };

  const handleYearChange = (e) => {
    const raw = e.target.value.replace(/\D/g, '').slice(0, 4);
    pushChange({ ...dateState, year: raw, qualifier: dateState.qualifier === 'unknown' ? 'exact' : dateState.qualifier });
  };

  const handleQualifierChange = (q) => {
    if (q === 'unknown') {
      pushChange({ ...dateState, year: '', month: '', day: '', qualifier: 'unknown' });
      return;
    }
    pushChange({ ...dateState, qualifier: q });
  };

  const handleDecadeSelect = (startYear) => {
    pushChange({
      ...dateState,
      year: String(startYear),
      month: '',
      day: '',
      qualifier: 'exact',
    });
    setShowDecades(false);
  };

  const months = useMemo(
    () => [
      { value: '01', label: 'January' },
      { value: '02', label: 'February' },
      { value: '03', label: 'March' },
      { value: '04', label: 'April' },
      { value: '05', label: 'May' },
      { value: '06', label: 'June' },
      { value: '07', label: 'July' },
      { value: '08', label: 'August' },
      { value: '09', label: 'September' },
      { value: '10', label: 'October' },
      { value: '11', label: 'November' },
      { value: '12', label: 'December' },
    ],
    []
  );

  const days = useMemo(
    () =>
      Array.from({ length: 31 }, (_, i) => ({
        value: String(i + 1).padStart(2, '0'),
        label: String(i + 1),
      })),
    []
  );

  const helperText = () => {
    if (mode === 'simple') {
      const y = (dateState.year || '').replace(/\D/g, '');
      if (!y) return 'A year is enough. You can refine this later from their profile.';
      return `Year: ${y}${y.length < 4 ? ' (still typing)' : ''}`;
    }
    if (dateState.qualifier === 'unknown') return 'Year left blank. You can refine this later from their profile.';
    const y = (dateState.year || '').replace(/\D/g, '');
    if (!y) return 'Type a year (1–4 digits while entering), or use decade shortcuts below.';
    if (dateState.qualifier === 'circa') return `Circa ${y}. Stored as an approximate year.`;
    if (dateState.qualifier === 'before') return `Before ${y}. Useful when the record only gives an upper bound.`;
    if (dateState.qualifier === 'after') return `After ${y}. Useful when the record only gives a lower bound.`;
    if (dateState.qualifier === 'estimated') return `Estimated around ${y}.`;
    if (dateState.month && dateState.day) return `Full date: ${y}-${dateState.month}-${dateState.day}`;
    if (dateState.month) return `Year and month: ${y}-${dateState.month}`;
    return `Year entry: ${y}${y.length < 4 ? ' (still typing)' : ''}`;
  };

  const yearField = (
    <div className="relative min-w-0 flex-1 space-y-1">
      <span className="text-[11px] text-muted-foreground">Year</span>
      <Input
        type="text"
        inputMode="numeric"
        autoComplete="off"
        placeholder={placeholder}
        value={dateState.qualifier === 'unknown' ? '' : dateState.year}
        onChange={handleYearChange}
        disabled={dateState.qualifier === 'unknown'}
        className={cn('font-mono tabular-nums', mode === 'simple' ? 'h-10' : 'text-lg')}
        maxLength={4}
        data-testid={`${testId}-year`}
      />
    </div>
  );

  const advancedControls = (
    <>
      <div className="w-full shrink-0 space-y-1 sm:w-[13.5rem]">
        <span className="text-[11px] text-muted-foreground">How certain is the year?</span>
        <Select value={dateState.qualifier} onValueChange={handleQualifierChange}>
          <SelectTrigger className="h-10" data-testid={`${testId}-qualifier`}>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {QUALIFIERS.map((q) => (
              <SelectItem key={q.value} value={q.value}>
                {q.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <Button
        type="button"
        variant="outline"
        size="icon"
        className="h-10 w-10 shrink-0 self-end"
        onClick={() => setShowDecades(!showDecades)}
        title="Optional decade shortcuts"
        data-testid={`${testId}-decade-toggle`}
      >
        {showDecades ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
      </Button>
    </>
  );

  const decadePanel = showDecades ? (
    <div className="rounded-lg border border-border/40 bg-muted/20 p-3" data-testid={`${testId}-decades`}>
      <p className="mb-2 text-xs text-muted-foreground">
        Optional shortcuts. They only prefill the year; typing always overrides.
      </p>
      <div className="flex flex-wrap gap-1.5">
        {primaryDecades.map((y) => (
          <Button
            key={y}
            type="button"
            variant="outline"
            size="sm"
            className="h-7 px-2 text-xs"
            onClick={() => handleDecadeSelect(y)}
            data-testid={`${testId}-decade-y-${y}`}
          >
            {decadeLabel(y)}
          </Button>
        ))}
      </div>
      <button
        type="button"
        className="mt-2 text-xs text-primary hover:underline"
        onClick={() => setShowOlderDecades(!showOlderDecades)}
        data-testid={`${testId}-toggle-older-decades`}
      >
        {showOlderDecades ? 'Hide 1400s–1960s' : 'Show 1400s–1960s'}
      </button>
      {showOlderDecades ? (
        <div className="mt-2 flex flex-wrap gap-1.5 border-t border-border/30 pt-2">
          {secondaryDecades.map((y) => (
            <Button
              key={y}
              type="button"
              variant="outline"
              size="sm"
              className="h-7 px-2 text-xs"
              onClick={() => handleDecadeSelect(y)}
              data-testid={`${testId}-decade-secondary-${y}`}
            >
              {decadeLabel(y)}
            </Button>
          ))}
        </div>
      ) : null}
      <button
        type="button"
        className="mt-2 text-xs text-primary hover:underline"
        onClick={() => setShowAncientDecades(!showAncientDecades)}
        data-testid={`${testId}-toggle-ancient-decades`}
      >
        {showAncientDecades ? 'Hide very early centuries (100s–1390s)' : 'Show very early centuries (100s–1390s)'}
      </button>
      {showAncientDecades ? (
        <div className="mt-2 flex max-h-40 flex-wrap gap-1.5 overflow-y-auto border-t border-border/30 pt-2 [scrollbar-width:thin]">
          {ancientDecades.map((y) => (
            <Button
              key={y}
              type="button"
              variant="outline"
              size="sm"
              className="h-7 px-2 text-xs"
              onClick={() => handleDecadeSelect(y)}
              data-testid={`${testId}-decade-ancient-${y}`}
            >
              {decadeLabel(y)}
            </Button>
          ))}
        </div>
      ) : null}
    </div>
  ) : null;

  const monthDayDetails = (
    <details className="group rounded-lg border border-border/35 bg-background/40">
      <summary className="cursor-pointer list-none px-3 py-2 text-sm text-primary hover:underline [&::-webkit-details-marker]:hidden">
        <span className="inline-flex items-center gap-2">
          <Calendar className="h-3.5 w-3.5" aria-hidden />
          Month &amp; day (optional)
        </span>
      </summary>
      <div className="border-t border-border/30 px-3 pb-3 pt-2">
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label className="text-xs text-muted-foreground">Month</Label>
            <select
              value={dateState.month}
              onChange={(e) => pushChange({ ...dateState, month: e.target.value })}
              className="mt-1 h-10 w-full rounded-md border border-input bg-background px-3 text-sm"
              data-testid={`${testId}-month`}
            >
              <option value="">—</option>
              {months.map((m) => (
                <option key={m.value} value={m.value}>
                  {m.label}
                </option>
              ))}
            </select>
          </div>
          <div>
            <Label className="text-xs text-muted-foreground">Day</Label>
            <select
              value={dateState.day}
              onChange={(e) => pushChange({ ...dateState, day: e.target.value })}
              className="mt-1 h-10 w-full rounded-md border border-input bg-background px-3 text-sm"
              data-testid={`${testId}-day`}
            >
              <option value="">—</option>
              {days.map((d) => (
                <option key={d.value} value={d.value}>
                  {d.label}
                </option>
              ))}
            </select>
          </div>
        </div>
        <p className="mt-2 text-[11px] leading-relaxed text-muted-foreground">
          Leave month/day empty for year-only records.
        </p>
      </div>
    </details>
  );

  return (
    <div className="space-y-3" data-testid={testId}>
      <Label className="text-sm font-medium">
        {label}
        {showDeathContext ? (
          <span className="mt-0.5 block text-xs font-normal text-muted-foreground">Leave blank if still living.</span>
        ) : null}
      </Label>

      {mode === 'simple' ? (
        yearField
      ) : collapseAdvanced ? (
        <>
          <div className="flex flex-col gap-2 sm:flex-row sm:items-end">{yearField}</div>
          <details className="rounded-lg border border-border/35 bg-muted/10">
            <summary className="cursor-pointer list-none px-3 py-2.5 text-sm font-medium text-foreground [&::-webkit-details-marker]:hidden">
              More date detail (optional)
            </summary>
            <div className="space-y-3 border-t border-border/30 px-3 pb-3 pt-3">
              <div className="flex flex-col gap-2 sm:flex-row sm:items-end">{advancedControls}</div>
              {decadePanel}
              {monthDayDetails}
            </div>
          </details>
        </>
      ) : (
        <>
          <div className="flex flex-col gap-2 sm:flex-row sm:items-end">
            {yearField}
            {advancedControls}
          </div>
          {decadePanel}
          {monthDayDetails}
        </>
      )}

      <p className="text-xs leading-relaxed text-muted-foreground/80">{helperText()}</p>
    </div>
  );
};

export default SmartDateInput;
